import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Wrench, RefreshCw, ShieldAlert, LogOut, UserRound } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 20000;

const fmt = (iso) => (iso ? new Date(iso).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '');

// Uygulama geneli bakım modu: admin dışı kullanıcılar tam ekran uyarı görür, admin alt köşede şerit görür
export default function MaintenanceGate() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [state, setState] = useState({ enabled: false, message: '', started_at: null });
  const [busy, setBusy] = useState(false);

  const cikisYap = async () => {
    setBusy(true);
    try { await logout(); } finally { setBusy(false); navigate('/login', { replace: true }); }
  };

  const refresh = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/settings/maintenance`);
      setState(res.data || { enabled: false });
    } catch { /* backend deploy anında ulaşılamayabilir — mevcut durumu koru */ }
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, POLL_MS);
    const onVis = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', onVis);
    const id = axios.interceptors.response.use(
      (r) => r,
      (err) => {
        if (err?.response?.status === 503 && err.response.data?.maintenance) {
          toast.error('Sistem bakımda — işleminiz kaydedilmedi', { id: 'maintenance-503' });
          refresh();
        }
        return Promise.reject(err);
      }
    );
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', onVis); axios.interceptors.response.eject(id); };
  }, [refresh]);

  // Admin panelde zaten panel var; şerit orada gösterilmez
  if (!state.enabled) return null;
  const isAdmin = !!user?.is_admin;

  if (isAdmin) {
    if (location.pathname.startsWith('/site-admin')) return null;
    const kapat = async () => {
      if (!window.confirm('Bakım modu kapatılsın mı? Kullanıcılar siteyi tekrar kullanabilecek.')) return;
      setBusy(true);
      try { await axios.put(`${API}/settings/maintenance`, { enabled: false }, { withCredentials: true }); await refresh(); toast.success('Bakım modu kapatıldı'); }
      catch (e) { toast.error('Kapatılamadı: ' + (e.response?.data?.detail || e.message)); }
      finally { setBusy(false); }
    };
    return (
      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[9000] flex items-center gap-3 rounded-full border border-amber-500/60 bg-amber-950/95 backdrop-blur px-4 py-2 shadow-2xl shadow-amber-900/40" data-testid="maintenance-admin-banner">
        <ShieldAlert className="w-4 h-4 text-amber-400 animate-pulse" />
        <span className="text-xs text-amber-200">BAKIM MODU AÇIK — kullanıcılar siteyi kullanamıyor{state.started_at ? ` (${fmt(state.started_at)})` : ''}</span>
        <a href="/site-admin" className="text-xs text-amber-400 underline hover:text-amber-300" data-testid="maintenance-admin-panel-link">Panel</a>
        <Button size="sm" onClick={kapat} disabled={busy} className="h-7 rounded-full bg-amber-600 hover:bg-amber-700 text-white text-xs" data-testid="maintenance-admin-off-btn">Bakımı Kapat</Button>
      </div>
    );
  }

  // Giriş yapmamış kullanıcı: login sayfasında engellemeden uyar (admin giriş yapabilmeli)
  if (!user) {
    return (
      <div className="fixed top-0 inset-x-0 z-[9000] bg-amber-600 text-black text-center text-sm font-medium py-2 px-4 flex items-center justify-center gap-2" data-testid="maintenance-login-banner">
        <Wrench className="w-4 h-4" /> Site şu anda bakımdadır — yalnızca yöneticiler giriş yapabilir.
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[9999] bg-[#0a0a0a] text-white flex items-center justify-center p-6" data-testid="maintenance-overlay" role="alertdialog" aria-live="assertive">
      <div className="absolute inset-0 opacity-[0.07] bg-[radial-gradient(circle_at_30%_20%,#f59e0b,transparent_40%),radial-gradient(circle_at_80%_80%,#f59e0b,transparent_35%)]" />
      <div className="absolute top-4 right-4 sm:top-6 sm:right-8 flex items-center gap-3">
        <span className="hidden sm:inline-flex items-center gap-2 text-xs text-gray-400" data-testid="maintenance-user-name">
          <UserRound className="w-3.5 h-3.5" /> {user.name || user.username}
        </span>
        <Button size="sm" variant="outline" onClick={cikisYap} disabled={busy} className="rounded-full border-gray-700 bg-gray-900/70 text-gray-200 hover:bg-gray-800 hover:text-white" data-testid="maintenance-logout-btn">
          <LogOut className="w-4 h-4 mr-2" /> Çıkış Yap
        </Button>
      </div>
      <div className="relative max-w-xl w-full">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-14 h-14 rounded-2xl bg-amber-500/15 border border-amber-500/40 flex items-center justify-center">
            <Wrench className="w-7 h-7 text-amber-400 animate-[spin_6s_linear_infinite]" />
          </div>
          <span className="text-xs tracking-[0.3em] text-amber-400/80 uppercase">Planlı Bakım</span>
        </div>
        <h1 className="text-4xl sm:text-5xl font-light tracking-tight mb-4" data-testid="maintenance-title">Site bakım aşamasındadır</h1>
        <p className="text-gray-300 text-base leading-relaxed mb-6" data-testid="maintenance-message">{state.message}</p>
        {state.started_at && <p className="text-xs text-gray-500 mb-8" data-testid="maintenance-started-at">Bakım başlangıcı: {fmt(state.started_at)}</p>}
        <div className="flex flex-wrap items-center gap-3">
          <Button onClick={refresh} className="rounded-full bg-amber-600 hover:bg-amber-700 text-white" data-testid="maintenance-retry-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Yeniden Dene
          </Button>
          <span className="text-xs text-gray-500">Sayfa otomatik olarak her 20 saniyede kontrol eder; bakım bitince kaldığınız yerden devam edersiniz. Yönetici olarak giriş yapmak için sağ üstten çıkış yapın.</span>
        </div>
      </div>
    </div>
  );
}
